import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, MessageSquare, ExternalLink, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/tawk")({
  head: () => ({ meta: [{ title: "Live Chat — Admin — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminTawk,
});

const DISABLE_FLAG = "peopels:tawk:disabled";

function maskId(id: string | undefined): string {
  if (!id) return "(not set)";
  if (id.length <= 8) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

function readDisabled(): boolean {
  try { return localStorage.getItem(DISABLE_FLAG) === "1"; } catch { return false; }
}

function AdminTawk() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [status, setStatus] = useState<"online" | "away" | "offline" | "unknown">("unknown");

  const propertyId = import.meta.env.VITE_TAWK_PROPERTY_ID as string | undefined;
  const widgetId = (import.meta.env.VITE_TAWK_WIDGET_ID as string | undefined) ?? "default";

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/admin/tawk" } }); return; }
      setAuthChecked(true);
      setDisabled(readDisabled());
    })();
  }, [navigate]);

  // Poll for widget state.
  useEffect(() => {
    if (!authChecked) return;
    const t = window.setInterval(() => {
      const loaded = !!document.getElementById("tawk-to-script");
      setScriptLoaded(loaded);
      const s = window.Tawk_API?.getStatus?.();
      if (s === "online" || s === "away" || s === "offline") setStatus(s);
      else setStatus("unknown");
    }, 1000);
    return () => window.clearInterval(t);
  }, [authChecked]);

  const toggleDisabled = (next: boolean) => {
    try {
      if (next) localStorage.setItem(DISABLE_FLAG, "1");
      else localStorage.removeItem(DISABLE_FLAG);
    } catch { /* noop */ }
    setDisabled(next);
    toast.success(next ? "Live chat disabled in this browser" : "Live chat enabled — reload to reload widget");
  };

  const testWidget = () => {
    const api = window.Tawk_API;
    if (!api?.showWidget) {
      toast.error("Widget not loaded yet. Reload after enabling.");
      return;
    }
    api.showWidget();
    api.maximize?.();
    toast.success("Opened chat widget");
  };

  const hardReload = () => window.location.reload();

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const statusBadge =
    status === "online" ? { label: "Agents online", cls: "bg-emerald-100 text-emerald-800" } :
    status === "away" ? { label: "Agents away", cls: "bg-amber-100 text-amber-800" } :
    status === "offline" ? { label: "Agents offline", cls: "bg-zinc-200 text-zinc-800" } :
    { label: "Unknown", cls: "bg-muted text-muted-foreground" };

  return (
    <>
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl font-semibold tracking-tight">Live Chat (Tawk.to)</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Configure, test, and disable the customer-support widget. The property ID is
          read from <code className="rounded bg-muted px-1">VITE_TAWK_PROPERTY_ID</code>.
          See <a href="/docs/tawk-chat" className="underline">docs/tawk-chat.md</a> in the repo for
          full operations notes.
        </p>

        <section className="mt-8 rounded-lg border bg-card p-5">
          <h2 className="text-lg font-medium">Configuration</h2>
          <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">Property ID</dt>
              <dd className="mt-1 font-mono">{maskId(propertyId)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Widget ID</dt>
              <dd className="mt-1 font-mono">{widgetId}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Script loaded</dt>
              <dd className="mt-1 inline-flex items-center gap-1">
                {scriptLoaded
                  ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Loaded</>
                  : <><XCircle className="h-4 w-4 text-zinc-500" /> Not loaded</>}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Agent status</dt>
              <dd className="mt-1">
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge.cls}`}>
                  {statusBadge.label}
                </span>
              </dd>
            </div>
          </dl>
          {!propertyId && (
            <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              VITE_TAWK_PROPERTY_ID is not set — the widget will not load in this environment.
              Add it under environment variables and redeploy.
            </p>
          )}
        </section>

        <section className="mt-6 rounded-lg border bg-card p-5">
          <h2 className="text-lg font-medium">Actions</h2>
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Disable widget in this browser</p>
                <p className="text-xs text-muted-foreground">
                  Sets <code>{DISABLE_FLAG}</code> in localStorage. Take effect after reload.
                </p>
              </div>
              <Switch checked={disabled} onCheckedChange={toggleDisabled} aria-label="Disable live chat" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={testWidget} disabled={!scriptLoaded || disabled}>
                <MessageSquare className="mr-2 h-4 w-4" /> Test widget now
              </Button>
              <Button variant="outline" onClick={hardReload}>Reload page</Button>
              <a
                href="https://dashboard.tawk.to/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Tawk dashboard <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-lg border bg-card p-5">
          <h2 className="text-lg font-medium">Replace with another provider</h2>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Add the new provider's script in <code>src/components/site/&lt;Provider&gt;Chat.tsx</code>.</li>
            <li>Swap <code>&lt;TawkChat /&gt;</code> for <code>&lt;ProviderChat /&gt;</code> in <code>src/routes/__root.tsx</code>.</li>
            <li>Update CSP origins in <code>src/lib/security-headers.ts</code>.</li>
            <li>Remove the <code>VITE_TAWK_*</code> environment variables.</li>
          </ol>
        </section>
      </main>
      <Footer />
    </>
  );
}
